import { useEffect, useState } from "react";
import { api, uploadFile } from "@/lib/api";
import { PageHeader, StatusBadge, FileLink } from "@/components/common";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Loader2, Plus, GraduationCap, Printer, FileText, Check, Trash2, Eye, Upload } from "lucide-react";
import { toast } from "sonner";

const INTERVAL_LABELS = { yearly: "jährlich", biyearly: "alle 2 Jahre", quarterly: "vierteljährlich", once: "einmalig" };

function suggestNextDue(interval, from) {
  const d = new Date(from);
  if (interval === "yearly") d.setFullYear(d.getFullYear() + 1);
  else if (interval === "biyearly") d.setFullYear(d.getFullYear() + 2);
  else if (interval === "quarterly") d.setMonth(d.getMonth() + 3);
  else return "";
  return d.toISOString().slice(0, 10);
}

function fmtDate(d) { if (!d) return null; try { return new Date(d).toLocaleDateString("de-DE"); } catch { return d; } }

function expiryInfo(expiry) {
  if (!expiry) return null;
  const days = Math.ceil((new Date(expiry) - new Date()) / 86400000);
  if (days < 0) return { variant: "warning", text: `fällig seit ${fmtDate(expiry)}` };
  if (days <= 30) return { variant: "due", text: `fällig in ${days} T.` };
  return { variant: "done", text: `nächste Schulung: ${fmtDate(expiry)}` };
}

export default function SchulungenPage() {
  const [trainings, setTrainings] = useState([]);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState(null);      // training to view
  const [record, setRecord] = useState(null);   // training to record
  const [addOpen, setAddOpen] = useState(false);

  const load = async () => {
    const [t, r] = await Promise.all([api.get("/trainings"), api.get("/training-records")]);
    setTrainings(t.data); setRecords(r.data); setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const openPrint = (id, test) => window.open(`/schulung-druck/${id}${test ? "?test=1" : ""}`, "_blank");
  const delRecord = async (id) => { await api.delete(`/training-records/${id}`); load(); };
  const delTraining = async (id) => { if (!window.confirm("Diese Schulung löschen?")) return; await api.delete(`/trainings/${id}`); load(); };

  const lastRecordFor = (title) => records.find((r) => r.training_title === title);

  if (loading) return <div className="flex items-center gap-2 text-slate-500 py-12 justify-center"><Loader2 className="h-5 w-5 animate-spin" /> Laden …</div>;

  return (
    <div>
      <PageHeader title="Schulungen" subtitle="Pflichtschulungen selbst vermitteln, drucken, testen und abhaken">
        <Button data-testid="btn-add-training" variant="outline" onClick={() => setAddOpen(true)}>
          <Plus className="h-4 w-4 mr-1" /> Eigene Schulung
        </Button>
      </PageHeader>

      <h3 className="font-heading text-sm font-bold text-slate-700 mb-3">Schulungsvorlagen</h3>
      <div className="grid gap-3 sm:grid-cols-2 mb-8">
        {trainings.map((t) => {
          const last = lastRecordFor(t.title);
          const ex = last ? expiryInfo(last.next_due) : null;
          return (
            <div key={t.id} className="rounded-xl border border-slate-200 bg-white p-4 flex flex-col" data-testid="training-card">
              <div className="flex items-start gap-3">
                <div className="rounded-lg bg-teal-50 p-2"><GraduationCap className="h-5 w-5 text-teal-600" /></div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-slate-800">{t.title}</div>
                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    <StatusBadge variant="teal">{t.category}</StatusBadge>
                    <StatusBadge variant="neutral">{INTERVAL_LABELS[t.interval] || t.interval}</StatusBadge>
                    {ex && <StatusBadge variant={ex.variant}>{ex.text}</StatusBadge>}
                  </div>
                </div>
                {!t.is_seed && (
                  <button onClick={() => delTraining(t.id)} className="text-slate-300 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
                )}
              </div>
              {t.summary && <p className="text-sm text-slate-500 mt-2 flex-1">{t.summary}</p>}
              <div className="flex flex-wrap gap-2 mt-3">
                <Button size="sm" variant="outline" onClick={() => setView(t)} data-testid="btn-view-training"><Eye className="h-4 w-4 mr-1" /> Inhalt</Button>
                <Button size="sm" variant="outline" onClick={() => openPrint(t.id, false)} data-testid="btn-print-training"><Printer className="h-4 w-4 mr-1" /> Drucken</Button>
                {t.test_questions?.length > 0 && (
                  <Button size="sm" variant="outline" onClick={() => openPrint(t.id, true)} data-testid="btn-print-test"><FileText className="h-4 w-4 mr-1" /> Test</Button>
                )}
                <Button size="sm" onClick={() => setRecord(t)} data-testid="btn-record-training"><Check className="h-4 w-4 mr-1" /> Abhaken</Button>
              </div>
            </div>
          );
        })}
      </div>

      <h3 className="font-heading text-sm font-bold text-slate-700 mb-3">Durchgeführte Schulungen</h3>
      {records.length === 0 ? (
        <div className="text-center py-10 text-slate-400 border border-dashed border-slate-200 rounded-xl">Noch keine Schulung dokumentiert</div>
      ) : (
        <div className="space-y-2">
          {records.map((r) => {
            const ex = expiryInfo(r.next_due);
            return (
              <div key={r.id} className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3" data-testid="training-record-row">
                <Check className="h-5 w-5 text-emerald-500 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-slate-800 text-sm">{r.training_title}</div>
                  <div className="text-xs text-slate-500">
                    Durchgeführt am {fmtDate(r.conducted_at)} · Teilnehmer: {r.participants}{r.note ? ` · ${r.note}` : ""}
                  </div>
                </div>
                {ex && <StatusBadge variant={ex.variant}>{ex.text}</StatusBadge>}
                <FileLink fileId={r.file_id} label="Material" />
                <button onClick={() => delRecord(r.id)} className="text-slate-300 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
              </div>
            );
          })}
        </div>
      )}

      {view && <ViewDialog training={view} onClose={() => setView(null)} />}
      {record && <RecordDialog training={record} onClose={() => setRecord(null)} onDone={() => { setRecord(null); load(); }} />}
      {addOpen && <AddDialog onClose={() => setAddOpen(false)} onDone={() => { setAddOpen(false); load(); }} />}
    </div>
  );
}

function ViewDialog({ training, onClose }) {
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="training-view-dialog">
        <DialogHeader>
          <DialogTitle>{training.title}</DialogTitle>
          <DialogDescription>{training.category} · {INTERVAL_LABELS[training.interval] || training.interval}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {training.summary && <p className="text-sm text-slate-600">{training.summary}</p>}
          {training.sections.map((s, i) => (
            <div key={i}>
              <h4 className="font-semibold text-slate-800 text-sm mb-1">{s.heading}</h4>
              <ul className="list-disc pl-5 space-y-1 text-sm text-slate-600">
                {(s.points || []).map((p, j) => <li key={j}>{p}</li>)}
              </ul>
            </div>
          ))}
          {training.test_questions?.length > 0 && (
            <div>
              <h4 className="font-semibold text-slate-800 text-sm mb-1">Testfragen</h4>
              <ol className="list-decimal pl-5 space-y-1 text-sm text-slate-600">
                {training.test_questions.map((q, i) => <li key={i}>{q}</li>)}
              </ol>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Schließen</Button>
          <Button onClick={() => window.open(`/schulung-druck/${training.id}`, "_blank")}><Printer className="h-4 w-4 mr-1" /> Drucken</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RecordDialog({ training, onClose, onDone }) {
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    conducted_at: today, participants: "", note: "",
    next_due: suggestNextDue(training.interval, today),
  });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!form.participants.trim()) { toast.error("Bitte Teilnehmer angeben"); return; }
    setBusy(true);
    try {
      let fileId = null;
      if (file) { const res = await uploadFile(file); fileId = res.file_id; }
      await api.post("/training-records", {
        training_id: training.id, training_title: training.title,
        conducted_at: form.conducted_at, participants: form.participants,
        note: form.note || null, file_id: fileId, next_due: form.next_due || null,
      });
      toast.success("Schulung dokumentiert");
      onDone();
    } catch { toast.error("Fehler"); } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="training-record-dialog">
        <DialogHeader>
          <DialogTitle>Schulung abhaken</DialogTitle>
          <DialogDescription>{training.title}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Durchgeführt am</Label>
              <Input type="date" data-testid="input-record-date" value={form.conducted_at} onChange={(e) => setForm({ ...form, conducted_at: e.target.value, next_due: suggestNextDue(training.interval, e.target.value) })} /></div>
            <div className="space-y-1.5"><Label>Nächste Schulung fällig</Label>
              <Input type="date" data-testid="input-record-nextdue" value={form.next_due} onChange={(e) => setForm({ ...form, next_due: e.target.value })} /></div>
          </div>
          <div className="space-y-1.5"><Label>Teilnehmer *</Label>
            <Input data-testid="input-record-participants" value={form.participants} onChange={(e) => setForm({ ...form, participants: e.target.value })} placeholder="Namen der Teilnehmer" /></div>
          <div className="space-y-1.5"><Label>Bemerkung</Label>
            <Textarea data-testid="input-record-note" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="z. B. Test bestanden, Fragen geklärt" /></div>
          <div className="space-y-1.5"><Label>Nachweis / ausgefüllter Test (optional)</Label>
            <label className="flex items-center gap-2 rounded-lg border border-dashed border-slate-300 px-3 py-2.5 text-sm text-slate-600 cursor-pointer hover:border-teal-400">
              <Upload className="h-4 w-4" /> {file ? file.name : "Datei auswählen (Foto/PDF)"}
              <input type="file" accept="image/*,application/pdf" className="hidden" data-testid="input-record-file" onChange={(e) => setFile(e.target.files?.[0] || null)} />
            </label></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Abbrechen</Button>
          <Button data-testid="btn-submit-record" onClick={submit} disabled={busy}>{busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />} Als erledigt speichern</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AddDialog({ onClose, onDone }) {
  const [form, setForm] = useState({ title: "", category: "", interval: "yearly", summary: "", content: "", questions: "" });
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!form.title.trim()) { toast.error("Titel erforderlich"); return; }
    setBusy(true);
    try {
      const points = form.content.split("\n").map((l) => l.trim()).filter(Boolean);
      const questions = form.questions.split("\n").map((l) => l.trim()).filter(Boolean);
      await api.post("/trainings", {
        title: form.title, category: form.category || "Sonstiges", interval: form.interval,
        summary: form.summary || null,
        sections: points.length ? [{ heading: "Inhalt", points }] : [],
        test_questions: questions,
      });
      toast.success("Schulung angelegt");
      onDone();
    } catch { toast.error("Fehler"); } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto" data-testid="training-add-dialog">
        <DialogHeader>
          <DialogTitle>Eigene Schulung anlegen</DialogTitle>
          <DialogDescription>Inhalte z. B. aus dem Netz recherchieren und hier einfügen</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Titel *</Label>
              <Input data-testid="input-add-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Kategorie</Label>
              <Input data-testid="input-add-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="z. B. Brandschutz" /></div>
          </div>
          <div className="space-y-1.5"><Label>Intervall</Label>
            <Select value={form.interval} onValueChange={(v) => setForm({ ...form, interval: v })}>
              <SelectTrigger data-testid="select-add-interval"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="yearly">jährlich</SelectItem>
                <SelectItem value="biyearly">alle 2 Jahre</SelectItem>
                <SelectItem value="quarterly">vierteljährlich</SelectItem>
                <SelectItem value="once">einmalig</SelectItem>
              </SelectContent>
            </Select></div>
          <div className="space-y-1.5"><Label>Kurzbeschreibung</Label>
            <Input value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} /></div>
          <div className="space-y-1.5"><Label>Inhalt (ein Punkt pro Zeile)</Label>
            <Textarea data-testid="input-add-content" rows={5} value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} placeholder={"Regel 1\nRegel 2\n..."} /></div>
          <div className="space-y-1.5"><Label>Testfragen (eine Frage pro Zeile, optional)</Label>
            <Textarea data-testid="input-add-questions" rows={3} value={form.questions} onChange={(e) => setForm({ ...form, questions: e.target.value })} placeholder={"Frage 1?\nFrage 2?"} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Abbrechen</Button>
          <Button data-testid="btn-submit-add-training" onClick={submit} disabled={busy}>{busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />} Anlegen</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
